import { TimeDelay, TriggerTemplate, SmsTriggerRule } from '@/types/sms-triggers';

/**
 * Convert time value to human-readable format with proper unit conversion
 * Examples:
 * - 0 minutes → "immediately"
 * - 30 minutes → "30 minutes"
 * - 166 hours → "6 days, 22 hours"
 * - 14 days → "2 weeks"
 */
export function formatTimeDelay(timeDelay: TimeDelay | null | undefined): string {
  if (!timeDelay || timeDelay.value === 0) {
    return 'immediately';
  }

  const { value, unit } = timeDelay;

  // Convert to minutes for easier calculation
  let totalMinutes = value;
  switch (unit) {
    case 'hours':
      totalMinutes = value * 60;
      break;
    case 'days':
      totalMinutes = value * 24 * 60;
      break;
    case 'weeks':
      totalMinutes = value * 7 * 24 * 60;
      break;
  }

  // Convert to most appropriate units
  const weeks = Math.floor(totalMinutes / (7 * 24 * 60));
  const remainingAfterWeeks = totalMinutes % (7 * 24 * 60);

  const days = Math.floor(remainingAfterWeeks / (24 * 60));
  const remainingAfterDays = remainingAfterWeeks % (24 * 60);

  const hours = Math.floor(remainingAfterDays / 60);
  const minutes = remainingAfterDays % 60;

  // Build the output string
  const parts: string[] = [];

  if (weeks > 0) {
    parts.push(`${weeks} ${weeks === 1 ? 'week' : 'weeks'}`);
  }
  if (days > 0) {
    parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  }
  if (hours > 0 && weeks === 0) { // Only show hours if not showing weeks
    parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  }
  if (minutes > 0 && weeks === 0 && days === 0) { // Only show minutes for smaller durations
    parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
  }

  // If we have multiple parts, join with comma
  if (parts.length === 0) {
    // Edge case: if all values are 0 but we got here, show original
    return `${value} ${unit}`;
  } else if (parts.length === 1) {
    return parts[0];
  } else if (parts.length === 2) {
    return `${parts[0]}, ${parts[1]}`;
  } else {
    // For 3+ parts, just show the two largest units
    return `${parts[0]}, ${parts[1]}`;
  }
}

/**
 * Get timing message based on trigger type
 */
export function getTimingMessage(
  trigger: TriggerTemplate | SmsTriggerRule | { trigger_type: string; time_delay?: TimeDelay; default_time_delay?: TimeDelay; default_conditions?: any[]; rule_conditions?: any },
  formattedDelay?: string
): string {
  const triggerType = trigger.trigger_type;

  // Get the formatted delay if not provided
  if (!formattedDelay) {
    const timeDelay = (trigger as any).default_time_delay || (trigger as any).time_delay;
    formattedDelay = formatTimeDelay(timeDelay);
  }

  if (triggerType === 're_engagement' || triggerType === 'customer_inactive') {
    // For re-engagement triggers, look for days_since_last_order in conditions
    let daysSinceLastOrder: number | null = null;

    // Check both template format and database format
    const conditions = (trigger as any).default_conditions ||
                      (trigger as any).rule_conditions?.conditions ||
                      [];

    // For template format (array of conditions)
    if (Array.isArray(conditions)) {
      const daysCondition = conditions.find((c: any) => c.field === 'days_since_last_order');
      if (daysCondition) {
        daysSinceLastOrder = daysCondition.value;
      }
    } else if (typeof conditions === 'object' && conditions !== null) {
      // For database format (object with field names as keys)
      if (conditions.days_since_last_order?.value) {
        daysSinceLastOrder = conditions.days_since_last_order.value;
      }
    }

    if (daysSinceLastOrder !== null) {
      if (formattedDelay === 'immediately') {
        return `Message will be sent immediately when a customer hasn't ordered for ${daysSinceLastOrder} ${daysSinceLastOrder === 1 ? 'day' : 'days'}`;
      } else {
        return `Message will be sent ${formattedDelay} before customer's typical order time, ${daysSinceLastOrder} ${daysSinceLastOrder === 1 ? 'day' : 'days'} after they last placed an order`;
      }
    }
  }

  // Default for milestone/customer_activity_update triggers
  if (formattedDelay === 'immediately') {
    return 'Message will be sent immediately after conditions are met';
  }
  return `Message will be sent ${formattedDelay} after conditions are met`;
}

/**
 * Simple time display for lists and tables
 * Used when you just need the time value without the full message
 */
export function formatTimeDelaySimple(timeDelay: TimeDelay | null | undefined): string {
  if (!timeDelay || timeDelay.value === 0) {
    return 'Immediate';
  }
  return formatTimeDelay(timeDelay);
}